import { GeographicOption, type GeographicTargetingState } from '@openx/types/targeting/geographic';
import { noop } from '@openx/utils/noop';
import { getGeoLocationDisplayName } from '@openx/utils/state/geoLocationSearch/utils';
import { useTranslation } from 'react-i18next';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import type { TargetingItemsProps } from '../../../types';
import { BoundingBoxItems } from '../../GeographicLocationItems/BoundingBoxItems';
import { CirclesItems } from '../../GeographicLocationItems/CircleItems';
import { ExcludeSubsetItems } from '../../GeographicLocationItems/ExcludeSubsetItems';
import { GeographicLocationItems } from '../../GeographicLocationItems/GeographicLocationItems';
import { LocationSourceItems } from '../../GeographicLocationItems/LocationSourceItems';
import { useGeographicItemsData } from '../../hooks/useGeographicItemsData';

type UseGeographicInventoryOptionsParams = {
  targetingParams: GeographicTargetingState;
  props: TargetingItemsProps<GeographicTargetingState>;
  disabledReasonMessage?: string;
};

export const useGeographicOptionsComponents = ({
  targetingParams,
  disabledReasonMessage,
  props,
}: UseGeographicInventoryOptionsParams) => {
  const { t } = useTranslation();
  const {
    excludes,
    includes,
    includesKeys,
    excludesKeys,
    locationCount,
    locationsSwitchState,
    isCirclesOptionSelected,
    isLocationSourceSelected,
    isBoundingBoxEmpty,
  } = useGeographicItemsData(targetingParams);

  return {
    [GeographicOption.INCLUDES]: locationCount
      ? () => (
          <div data-test="sub-section">
            <FiltersTitle
              onClear={noop}
              title={t('Location [ {count} ]', { count: locationCount })}
              disabledReasonMessage={disabledReasonMessage}
            />

            {locationsSwitchState ? (
              <GeographicLocationItems
                locationKeys={excludesKeys}
                locations={excludes}
                readonly={true}
                handleChange={() => noop}
                onLocationRemove={noop}
                locationsSwitchState={false}
                getGeoLocationDisplayName={getGeoLocationDisplayName}
              />
            ) : (
              <GeographicLocationItems
                locationKeys={includesKeys}
                locations={includes}
                readonly={true}
                handleChange={() => noop}
                onLocationRemove={noop}
                locationsSwitchState={true}
                getGeoLocationDisplayName={getGeoLocationDisplayName}
              />
            )}

            <ExcludeSubsetItems {...props} />
          </div>
        )
      : null,

    [GeographicOption.CIRCLES]: isCirclesOptionSelected
      ? () => (
          <CirclesItems
            isNotLastItem={false}
            targetingParams={targetingParams}
            readonly={true}
            onChange={noop}
            disabledReasonMessage={disabledReasonMessage}
          />
        )
      : null,

    [GeographicOption.BOUNDING_BOX]: !isBoundingBoxEmpty
      ? () => (
          <BoundingBoxItems
            targetingParams={targetingParams}
            readonly={true}
            onChange={noop}
            disabledReasonMessage={disabledReasonMessage}
          />
        )
      : null,

    [GeographicOption.SOURCE]: isLocationSourceSelected
      ? () => (
          <LocationSourceItems
            targetingParams={targetingParams}
            readonly={true}
            onChange={noop}
            disabledReasonMessage={disabledReasonMessage}
          />
        )
      : null,
  };
};
